import React, { useState, useEffect } from 'react';

/**
 * Modal pour créer/modifier une tâche personnelle
 * Utilisé par les chefs de département pour s'auto-assigner des tâches
 */
export default function PersonalTaskModal({ task, onSave, onClose }) {
  const [formData, setFormData] = useState({
    text: '',
    description: '',
    priority: 'normale',
    category: 'Admin',
    date_tache: new Date().toISOString().slice(0, 10),
    deadline: '',
  });

  useEffect(() => {
    if (task) {
      setFormData({
        text: task.text || task.titre || '',
        description: task.description || '',
        priority: task.priority || task.prio || 'normale',
        category: task.category || task.categorie || 'Admin',
        date_tache: task.date_tache || task.date || new Date().toISOString().slice(0, 10),
        deadline: task.deadline || task.dateEcheance || '',
      });
    }
  }, [task]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.text.trim()) {
      alert('Veuillez saisir l\'intitulé de la tâche');
      return;
    }

    onSave({
      ...formData,
      _id: task?.id || task?._id,
    });
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.7)',
        zIndex: 999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0d1b2a',
          border: '1px solid rgba(255,255,255,.1)',
          borderRadius: 14,
          padding: 24,
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20
        }}>
          <h3 style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#fff',
            margin: 0
          }}>
            {task ? 'Modifier la tâche' : 'Nouvelle tâche personnelle'}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#667',
              fontSize: 22,
              cursor: 'pointer',
              padding: 0
            }}
          >
            ✕
          </button>
        </div>

        {/* Formulaire */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Titre */}
          <div>
            <label style={{
              display: 'block',
              fontSize: 10,
              color: '#8a9bb0',
              textTransform: 'uppercase',
              letterSpacing: '.8px',
              marginBottom: 6,
              fontWeight: 600
            }}>
              Intitulé de la tâche <span style={{ color: '#ef8080' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.text}
              onChange={(e) => handleChange('text', e.target.value)}
              placeholder="Ex: Préparer le rapport mensuel"
              style={{
                width: '100%',
                background: 'rgba(255,255,255,.05)',
                border: '1.5px solid rgba(255,255,255,.1)',
                borderRadius: 8,
                padding: '10px 12px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = 'rgba(201,168,76,.5)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,.1)'}
            />
          </div>

          {/* Description */}
          <div>
            <label style={{
              display: 'block',
              fontSize: 10,
              color: '#8a9bb0',
              textTransform: 'uppercase',
              letterSpacing: '.8px',
              marginBottom: 6,
              fontWeight: 600
            }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Détails complémentaires..."
              rows={3}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,.05)',
                border: '1.5px solid rgba(255,255,255,.1)',
                borderRadius: 8,
                padding: '10px 12px',
                color: '#fff',
                fontSize: 13,
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.target.style.borderColor = 'rgba(201,168,76,.5)'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,.1)'}
            />
          </div>

          {/* Ligne: Priorité + Catégorie */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* Priorité */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 10,
                color: '#8a9bb0',
                textTransform: 'uppercase',
                letterSpacing: '.8px',
                marginBottom: 6,
                fontWeight: 600
              }}>
                Priorité
              </label>
              <select
                value={formData.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,.05)',
                  border: '1.5px solid rgba(255,255,255,.1)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="basse">Basse</option>
                <option value="normale">Normale</option>
                <option value="haute">Haute</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>

            {/* Catégorie */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 10,
                color: '#8a9bb0',
                textTransform: 'uppercase',
                letterSpacing: '.8px',
                marginBottom: 6,
                fontWeight: 600
              }}>
                Catégorie
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,.05)',
                  border: '1.5px solid rgba(255,255,255,.1)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="Admin">Admin</option>
                <option value="Pilotage">Pilotage</option>
                <option value="Projet">Projet</option>
                <option value="Finance">Finance</option>
                <option value="RH">RH</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
          </div>

          {/* Ligne: Date + Deadline */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* Date de la tâche */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 10,
                color: '#8a9bb0',
                textTransform: 'uppercase',
                letterSpacing: '.8px',
                marginBottom: 6,
                fontWeight: 600
              }}>
                Date
              </label>
              <input
                type="date"
                value={formData.date_tache}
                onChange={(e) => handleChange('date_tache', e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,.05)',
                  border: '1.5px solid rgba(255,255,255,.1)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
            </div>

            {/* Date limite */}
            <div>
              <label style={{
                display: 'block',
                fontSize: 10,
                color: '#8a9bb0',
                textTransform: 'uppercase',
                letterSpacing: '.8px',
                marginBottom: 6,
                fontWeight: 600
              }}>
                Date limite
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => handleChange('deadline', e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,.05)',
                  border: '1.5px solid rgba(255,255,255,.1)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'flex-end',
          marginTop: 24,
          paddingTop: 20,
          borderTop: '1px solid rgba(255,255,255,.07)'
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1.5px solid rgba(255,255,255,.15)',
              borderRadius: 8,
              padding: '9px 18px',
              color: '#888',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = 'rgba(255,255,255,.3)';
              e.target.style.color = '#aaa';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(255,255,255,.15)';
              e.target.style.color = '#888';
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            style={{
              background: 'linear-gradient(135deg, #C9A84C, #8a6028)',
              border: 'none',
              borderRadius: 8,
              padding: '9px 18px',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              boxShadow: '0 2px 8px rgba(201,168,76,.2)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 12px rgba(201,168,76,.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(201,168,76,.2)';
            }}
          >
            {task ? 'Modifier' : 'Créer la tâche'}
          </button>
        </div>
      </div>
    </div>
  );
}
